import type { LocationIdentifier, UserOrganization, UserProfile } from "~/types/api";
import type { OrganizationIdentifier } from "~/composables/useCurrentOrganization";
import { locationIdsMatch } from "~/utils/locationIdentifier";
import { decidePatientContextSelection, type PatientContextSelectionDecision } from "~/utils/patientContextSelection";

export type ContextSelectionDecision =
  | PatientContextSelectionDecision
  | { type: "select"; organizationId: OrganizationIdentifier; locationId: LocationIdentifier };

export interface ContextSelectionInput {
  organizations: UserOrganization[];
  profiles: UserProfile[];
  currentOrganizationId: OrganizationIdentifier | null | undefined;
  currentLocationId: LocationIdentifier | null | undefined;
  routeLocationId?: LocationIdentifier;
}

const findOrganizationForLocation = (
  organizations: UserOrganization[],
  locationId: LocationIdentifier,
): UserOrganization | undefined =>
  organizations.find((organization) =>
    organization.locations.some((location) => locationIdsMatch(location.id, locationId)),
  );

const findOrganization = (
  organizations: UserOrganization[],
  organizationId: OrganizationIdentifier | null | undefined,
): UserOrganization | undefined => organizations.find((organization) => organization.id === organizationId);

export function contextLocationsForOrganization(
  organizations: UserOrganization[],
  organizationId: OrganizationIdentifier | null | undefined,
) {
  return findOrganization(organizations, organizationId)?.locations ?? [];
}

export function decideContextSelection(input: ContextSelectionInput): ContextSelectionDecision {
  const { organizations, profiles, currentOrganizationId, currentLocationId, routeLocationId } = input;

  if (organizations.length === 0) {
    return decidePatientContextSelection({ profiles, currentLocationId, routeLocationId });
  }

  if (routeLocationId !== undefined) {
    const routeOrganization = findOrganizationForLocation(organizations, routeLocationId);

    if (!routeOrganization) {
      return { type: "switcher" };
    }

    return { type: "select", organizationId: routeOrganization.id, locationId: routeLocationId };
  }

  const selectedOrganization = findOrganization(organizations, currentOrganizationId);

  if (!selectedOrganization) {
    const onlyOrganization = organizations.length === 1 ? organizations[0] : undefined;
    const onlyLocation = onlyOrganization?.locations.length === 1 ? onlyOrganization.locations[0] : undefined;

    if (onlyOrganization && onlyLocation) {
      return { type: "select", organizationId: onlyOrganization.id, locationId: Number(onlyLocation.id) };
    }

    return { type: "switcher" };
  }

  const selectedLocationIsValid = selectedOrganization.locations.some(
    (location) =>
      currentLocationId !== null &&
      currentLocationId !== undefined &&
      locationIdsMatch(location.id, currentLocationId),
  );

  if (selectedLocationIsValid) {
    return { type: "none" };
  }

  if (selectedOrganization.locations.length === 1 && selectedOrganization.locations[0]) {
    return {
      type: "select",
      organizationId: selectedOrganization.id,
      locationId: Number(selectedOrganization.locations[0].id),
    };
  }

  return { type: "switcher" };
}
